import { Vec3, scale, normalize, lengthSq } from '../math/vec3'
import { CelestialBody } from '../world/body'
import { densityAt } from '../world/atmosphere'
import { Vessel } from '../vessel/vessel'
import { PartCatalog, partDef } from '../vessel/catalog'

export type StatoParacadute = 'chiuso' | 'parziale' | 'aperto' | 'strappato'

export interface Paracadute {
  readonly partId: string
  stato: StatoParacadute
}

export function paracaduteDi(vessel: Vessel, catalog: PartCatalog): Paracadute[] {
  return vessel.parts
    .filter((p) => partDef(catalog, p.defId).parachute)
    .map((p) => ({ partId: p.id, stato: 'chiuso' as StatoParacadute }))
}

/**
 * Aggiorna lo stato dei paracadute.
 *
 * Aprirli e in due tempi: prima una vela parziale che frena senza strappare,
 * poi la piena sotto una certa quota. Aprirli troppo veloce li strappa — che
 * e il modo piu comune di perdere una capsula altrimenti sana.
 */
export function aggiornaParacadute(
  paracadute: readonly Paracadute[],
  vessel: Vessel,
  catalog: PartCatalog,
  quota: number,
  pressioneDinamica: number,
  aperturaRichiesta: boolean,
): Paracadute[] {
  return paracadute.map((p): Paracadute => {
    if (p.stato === 'strappato') return p
    const parte = vessel.parts.find((x) => x.id === p.partId)
    if (!parte) return p
    const def = partDef(catalog, parte.defId).parachute!

    if (p.stato === 'chiuso') {
      if (!aperturaRichiesta) return p
      // lo strappo si decide NELL'ISTANTE dell'apertura: aprire a 300 m/s
      // strappa subito, non al passo dopo
      return pressioneDinamica > def.pressioneStrappo
        ? { ...p, stato: 'strappato' }
        : { ...p, stato: 'parziale' }
    }

    if (pressioneDinamica > def.pressioneStrappo) return { ...p, stato: 'strappato' }
    if (p.stato === 'parziale' && quota < def.quotaApertura) return { ...p, stato: 'aperto' }
    return p
  })
}

/** Superficie efficace di un paracadute nel suo stato. */
export function areaParacadute(stato: StatoParacadute, def: { areaAperto: number; areaParziale: number }): number {
  switch (stato) {
    case 'aperto': return def.areaAperto
    case 'parziale': return def.areaParziale
    default: return 0
  }
}

export interface ForzaParacadute {
  readonly partId: string
  readonly force: Vec3
  readonly point: Vec3
}

/**
 * Resistenza dei paracadute aperti. `airflowBody` e la velocita dell'aria
 * rispetto al veicolo, come in aeroForces.
 */
export function forzeParacadute(
  paracadute: readonly Paracadute[],
  vessel: Vessel,
  catalog: PartCatalog,
  airflowBody: Vec3,
  body: CelestialBody,
  quota: number,
): ForzaParacadute[] {
  const densita = densityAt(body.atmosphere, quota)
  const v2 = lengthSq(airflowBody)
  if (densita <= 0 || v2 <= 0) return []

  const q = 0.5 * densita * v2
  const dir = normalize(airflowBody)
  const out: ForzaParacadute[] = []

  for (const p of paracadute) {
    const parte = vessel.parts.find((x) => x.id === p.partId)
    if (!parte) continue
    const def = partDef(catalog, parte.defId).parachute
    if (!def) continue
    const area = areaParacadute(p.stato, def)
    if (area <= 0) continue
    // Cd di una calotta: circa 1.4
    out.push({ partId: p.partId, force: scale(dir, q * area * 1.4), point: parte.position })
  }
  return out
}
